import React, { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { cn } from './utils';

type CoreSkeletonProps = {
  children?: React.ReactNode;
  inline?: boolean;
  className?: string;
};

const CoreSkeleton: React.FC<CoreSkeletonProps> = ({ className = '', inline = false, children }) => {
  const inlineStyles = inline ? 'absolute inset-0 w-full h-full' : '';
  return (
    <div data-testid="skeleton" className={cn('bg-skeleton rounded-sm animate-pulse', inlineStyles, className)}>
      {children}
    </div>
  );
};

type SkeletonProps = {
  /** Whether to show skeleton (true) or children (false) */
  isLoading?: boolean;
  /** Keep children's space while showing skeleton overlay */
  preserveSpace?: boolean;
  /** Class name for the wrapper element (content-wrapping mode only) */
  wrapperClassName?: string;
  /** Children to render when not loading */
  children?: React.ReactNode;
  /** Skip enter/exit animation (useful for charts/heavy components) */
  disableEnterExitAnimation?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

function Skeleton({
  className,
  isLoading,
  preserveSpace,
  wrapperClassName,
  children,
  disableEnterExitAnimation = false,
  ...props
}: SkeletonProps) {
  // Original behavior: no isLoading prop = basic skeleton block
  if (isLoading === undefined) {
    return <div data-testid="skeleton" className={cn('animate-pulse rounded-sm bg-muted', className)} {...props} />;
  }

  // Use the animated skeleton for content wrapping behavior
  return (
    <AnimatedSkeleton
      isLoading={isLoading}
      preserveSpace={preserveSpace}
      wrapperClassName={wrapperClassName}
      className={className}
      disableEnterExitAnimation={disableEnterExitAnimation}
      {...props}
    >
      {children}
    </AnimatedSkeleton>
  );
}

type AnimatedSkeletonProps = {
  isLoading: boolean;
  children?: React.ReactNode;
  className?: string;
  preserveSpace?: boolean;
  wrapperClassName?: string;
  disableEnterExitAnimation?: boolean;
};

const AnimatedSkeleton = React.memo((props: AnimatedSkeletonProps) => {
  const { isLoading, children, className = '', wrapperClassName = '', preserveSpace = false, disableEnterExitAnimation = false } = props;

  // Make the content visible immediately if it's not loading
  const [isVisible, setIsVisible] = useState(!isLoading);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevLoading = useRef(isLoading);

  useEffect(() => {
    if (isLoading) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // If loading just started, hide the content immediately:
      setIsVisible(false);
    } else if (prevLoading.current) {
      // If loading just ended, show the content after a delay (500ms):
      timerRef.current = setTimeout(() => {
        setIsVisible(true);
        timerRef.current = null;
      }, 500);
    }

    prevLoading.current = isLoading;

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;

        if (!isLoading) {
          setIsVisible(true);
        }
      }
    };
  }, [isLoading]);

  const pulseVariants = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
    },
    exit: {
      opacity: disableEnterExitAnimation ? 1 : 0,
    },
  };

  const renderView = () => {
    if (isVisible) {
      return (
        <motion.div
          key="actual"
          className={cn('inline-block', wrapperClassName)}
          variants={pulseVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {children}
        </motion.div>
      );
    }

    if (preserveSpace) {
      return (
        <motion.div
          className={cn('inline-block', wrapperClassName)}
          key="expect"
          variants={pulseVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <span className={cn('relative inline-block', className)}>
            <span className="invisible">{children}</span>
            <CoreSkeleton inline />
          </span>
        </motion.div>
      );
    }

    return (
      <motion.div
        className={cn('inline-block', wrapperClassName)}
        key="expect"
        variants={pulseVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <CoreSkeleton className={cn('min-w-[1em] min-h-[1em] align-middle w-fit', className)}>
          {children && <div className="invisible">{children}</div>}
        </CoreSkeleton>
      </motion.div>
    );
  };

  return <AnimatePresence mode="wait">{renderView()}</AnimatePresence>;
});

export { Skeleton };
